import React, { PropsWithChildren } from 'react'
import type { SetOptional } from 'type-fest'
import type { Article, Blog, BlogsSection as Settings } from '~/types/shopify'
import { useTranslations } from '../Locale'
import { Image } from '../Photos'
import { Section } from './Common'

interface Props {
	section: Settings
}

export const Articles: React.FC<Props> = ({ section }) => {
	const t = useTranslations('react.blogs')
	return (
		<Section tag={section.tag} className="space-y-12 py-24 lg:space-y-16 lg:py-28">
			<div className="text-center">
				<h2 className="font-bold">{section.title}</h2>
			</div>
			<ArticleGrid>
				{section.blogs?.map((article, i) => (
					<ArticleCard
						key={i}
						article={{
							title: article.title,
							handle: article.url.split('/').pop() ?? '',
							publishedAt: article.published_at,
							content: article.content ?? '',
							excerpt: article.excerpt ?? '',
							image: article.image
								? {
										id: '' as any,
										url: article.image,
										altText: article.title
									}
								: undefined
						}}
						showDate={section.settings.blog_show_date}
					/>
				))}
				{section.blogs?.length === 1 && <div />}
			</ArticleGrid>

			{section.blogs?.length === 0 && <p className="text-center">{t?.no_articles}</p>}
		</Section>
	)
}

export const ArticleGrid: React.FC<PropsWithChildren> = ({ children }) => (
	<div className="grid grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] justify-start gap-16 lg:grid-cols-[repeat(auto-fit,minmax(25rem,1fr))]">
		{children}
	</div>
)

export interface ArticleProps {
	article: SetOptional<Article, 'id' | 'image' | 'tags' | 'publishedAt'>
	blog?: Blog
	showDate?: boolean
}

export const ArticleCard: React.FC<ArticleProps> = ({ article, blog, showDate }) => {
	const t = useTranslations('react.blogs')
	return (
		<div>
			<a
				href={`${window.Shopify.routes.root}blogs/${blog?.handle}/${article.handle}`}
				className="space-y-4 text-inherit no-underline transition-opacity hover:opacity-80"
			>
				{article.image && (
					<div className="aspect-14/9 overflow-hidden rounded-lg">
						<Image src={article.image} alt={article.title} className="h-full w-full object-cover" />
					</div>
				)}

				{showDate && article.publishedAt && (
					<span className="absolute -top-10 right-2 text-xl font-semibold text-white">
						{new Date(article.publishedAt).toLocaleDateString()}
					</span>
				)}
				{blog?.title && <span className="text-base font-medium uppercase leading-none">{blog.title}</span>}
				<h3 className="text-4xl">{article.title}</h3>
				<p className="leading-snug">
					{article.excerpt && article.excerpt.trim().length > 0 ? (
						<span dangerouslySetInnerHTML={{ __html: article.excerpt }} />
					) : article.content.split(' ').length > 40 ? (
						<span>{article.content.split(' ').slice(0, 40).join(' ')}...</span>
					) : (
						<span>{article.content}</span>
					)}
				</p>
				<span className="btn rounded-2xl px-4 py-0 text-white">{t?.read_more}</span>
			</a>
		</div>
	)
}
